import moment from "moment";
import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { deleteCustomer, getCustomersByBusiness } from "../../../redux/reducers/customerSlice";
import {
  Card, Table, Badge, Form, Row, Col, Button,
  InputGroup, Pagination
} from "react-bootstrap";
import Spinner from "../../../components/Spinner";
import {
  LuSearch, LuUser, LuChevronLeft, LuChevronRight,
  LuCircleDot, LuTrash2, LuMail, LuArrowUpRight
} from "react-icons/lu";
import ConfirmModal from "../../../components/ConfirmModal";

const ITEMS_PER_PAGE_OPTIONS = [5, 10, 15, 20];

export default function CustomerList({ searchTerm = "" }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const selectedBusiness = useSelector((state) => state.business.selectedBusiness);
  const businessId = selectedBusiness?._id;
  const { customerList: customerdata, loading } = useSelector((state) => state.customer);

  useEffect(() => {
    if (businessId) dispatch(getCustomersByBusiness(businessId));
  }, [dispatch, businessId]);

  // --- LOCAL FILTER STATE (internal search + sort) ---
  const [localSearch, setLocalSearch] = useState("");
  const [sortOrder, setSortOrder] = useState("newest");

  // --- PAGINATION STATE ---
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);

  // --- DELETE STATE ---
  const [deletemodalshow, setDeletemodalshow] = useState(false);
  const [deleteid, setDeleteid] = useState(null);

  // --- DERIVED: filtered + sorted list ---
  // Merges external searchTerm (from parent) with internal localSearch
  const filteredCustomers = useMemo(() => {
    if (!customerdata) return [];

    const externalTerm = searchTerm?.toLowerCase() || "";
    const internalTerm = localSearch?.toLowerCase() || "";

    const filtered = customerdata.filter((customer) => {
      const name = customer?.name?.toLowerCase() || "";
      const email = customer?.email?.toLowerCase() || "";

      const matchesExternal =
        !externalTerm || name.includes(externalTerm) || email.includes(externalTerm);
      const matchesInternal =
        !internalTerm || name.includes(internalTerm) || email.includes(internalTerm);

      return matchesExternal && matchesInternal;
    });

    // Sort
    return [...filtered].sort((a, b) => {
      if (sortOrder === "newest")
        return new Date(b.createdAt) - new Date(a.createdAt);
      if (sortOrder === "oldest")
        return new Date(a.createdAt) - new Date(b.createdAt);
      if (sortOrder === "name_asc")
        return (a.name || "").localeCompare(b.name || "");
      if (sortOrder === "name_desc")
        return (b.name || "").localeCompare(a.name || "");
      return 0;
    });
  }, [customerdata, searchTerm, localSearch, sortOrder]);

  // --- PAGINATION ---
  const totalPages = Math.max(1, Math.ceil(filteredCustomers.length / limit));
  const paginatedCustomers = useMemo(() => {
    const start = (currentPage - 1) * limit;
    return filteredCustomers.slice(start, start + limit);
  }, [filteredCustomers, currentPage, limit]);

  useEffect(() => { setCurrentPage(1); }, [searchTerm, localSearch, sortOrder, limit]);

  const hasActiveFilters = localSearch || sortOrder !== "newest" || searchTerm;

  const clearFilters = () => {
    setLocalSearch("");
    setSortOrder("newest");
  };

  // --- DELETE ---
  const handleDelete = () => {
    dispatch(deleteCustomer(deleteid)).then((res) => {
      if (!res.error) setDeletemodalshow(false);
    });
  };

  // --- Avatar initials helper ---
  const getInitials = (name = "") =>
    name.split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase();

  const avatarColors = [
    "#28B0C4", "#6366f1", "#f59e0b", "#10b981",
    "#ef4444", "#8b5cf6", "#ec4899", "#14b8a6"
  ];
  const getAvatarColor = (id = "") =>
    avatarColors[id.charCodeAt(id.length - 1) % avatarColors.length];

  return (
    <div className="mt-4">

      {/* --- FILTER BAR --- */}
      <Card className="border-0 shadow-sm rounded-4 p-3 mb-4">
        <Row className="g-3 align-items-end">

          <Col lg={4} md={6}>
            <Form.Label className="small fw-bold text-muted mb-1">Search</Form.Label>
            <InputGroup className="bg-light rounded-pill overflow-hidden border-0">
              <InputGroup.Text className="bg-transparent border-0 pe-0 text-muted">
                <LuSearch size={16} />
              </InputGroup.Text>
              <Form.Control
                placeholder="Name or email..."
                className="bg-transparent border-0 shadow-none py-2 small"
                value={localSearch}
                onChange={(e) => setLocalSearch(e.target.value)}
              />
            </InputGroup>
          </Col>

          <Col lg={2} md={6}>
            <Form.Label className="small fw-bold text-muted mb-1">Sort By</Form.Label>
            <Form.Select
              className="bg-light border-0 rounded-pill shadow-none small fw-bold py-2"
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
            >
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
              <option value="name_asc">Name A → Z</option>
              <option value="name_desc">Name Z → A</option>
            </Form.Select>
          </Col>

          {hasActiveFilters && (
            <Col lg="auto">
              <Button
                variant="outline-secondary"
                className="rounded-pill px-3 small fw-bold py-2"
                onClick={clearFilters}
              >
                Clear
              </Button>
            </Col>
          )}

          <Col lg="auto" className="ms-auto">
            <div className="text-muted small fw-medium text-end">
              <strong>{filteredCustomers.length}</strong> of{" "}
              <strong>{customerdata?.length ?? 0}</strong> customers
            </div>
          </Col>

        </Row>
      </Card>

      {/* --- TABLE --- */}
      <Card className="border-0 shadow-sm rounded-4 overflow-hidden">
        <Card.Body className="p-0">
          <Table responsive hover className="align-middle mb-0">
            <thead className="bg-light text-muted small text-uppercase">
              <tr>
                <th className="ps-4 py-3 fw-semibold">Customer</th>
                <th className="fw-semibold">Email</th>
                <th className="fw-semibold">Joined</th>
                <th className="pe-4 text-end fw-semibold">Action</th>
              </tr>
            </thead>
            <tbody>
              {paginatedCustomers.length > 0 ? (
                paginatedCustomers.map((customer) => (
                  <tr key={customer._id}>

                    {/* Customer — avatar + name */}
                    <td className="ps-4 py-3">
                      <div className="d-flex align-items-center gap-3">
                        <div
                          className="rounded-circle d-flex align-items-center justify-content-center fw-bold text-white"
                          style={{
                            width: 36,
                            height: 36,
                            fontSize: "12px",
                            backgroundColor: getAvatarColor(customer._id),
                            flexShrink: 0
                          }}
                        >
                          {getInitials(customer.name)}
                        </div>
                        <div>
                          <div className="fw-bold small">{customer?.name}</div>
                          <div className="text-muted" style={{ fontSize: "11px" }}>
                            ID: #{customer?._id?.slice(-6).toUpperCase()}
                          </div>
                        </div>
                      </div>
                    </td>

                    {/* Email */}
                    <td>
                      <div className="d-flex align-items-center gap-2">
                        <LuMail size={13} className="text-muted" />
                        <span className="small">{customer?.email || "—"}</span>
                      </div>
                    </td>

                    {/* Joined date */}
                    <td>
                      <div className="small fw-semibold">
                        {moment(customer?.createdAt).format("MMM DD, YYYY")}
                      </div>
                      <div className="text-muted" style={{ fontSize: "11px" }}>
                        {moment(customer?.createdAt).fromNow()}
                      </div>
                    </td>

                    {/* Actions */}
                    <td className="pe-4 text-end">
                      <div className="d-flex justify-content-end align-items-center gap-2">
                        <Button
                          variant="light"
                          size="sm"
                          className="rounded-circle p-2"
                          title="View customer"
                          onClick={() => navigate(`/customer-detail/${customer._id}`)}
                        >
                          <LuArrowUpRight size={15} className="text-primary" />
                        </Button>
                        <Button
                          variant="light"
                          size="sm"
                          className="rounded-circle p-2"
                          title="Delete customer"
                          onClick={() => {
                            setDeleteid(customer?._id);
                            setDeletemodalshow(true);
                          }}
                        >
                          <LuTrash2 size={15} className="text-danger" />
                        </Button>
                      </div>
                    </td>

                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} className="text-center py-5 text-muted">
                    <LuCircleDot size={40} className="mb-2 opacity-25" />
                    <p className="mb-0 small">
                      {hasActiveFilters
                        ? "No customers match your search."
                        : "No customers found."}
                    </p>
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </Card.Body>

        {/* --- PAGINATION FOOTER --- */}
        {filteredCustomers.length > 0 && (
          <Card.Footer className="bg-white border-0 py-3 px-4 d-flex justify-content-between align-items-center flex-wrap gap-2">
            <div className="d-flex align-items-center gap-3">
              <div className="d-flex align-items-center gap-2">
                <small className="text-muted fw-medium">Rows per page:</small>
                <Form.Select
                  size="sm"
                  className="border-0 bg-light rounded-3 fw-bold py-1 px-2"
                  style={{ width: "70px", fontSize: "0.8rem" }}
                  value={limit}
                  onChange={(e) => {
                    setLimit(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                >
                  {ITEMS_PER_PAGE_OPTIONS.map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </Form.Select>
              </div>
              <small className="text-muted fw-medium border-start ps-3">
                Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
                &nbsp;({filteredCustomers.length} total)
              </small>
            </div>

            <Pagination
              className="mb-0 gap-1"
              style={{
                "--bs-pagination-active-bg": "#28B0C4",
                "--bs-pagination-active-border-color": "#28B0C4",
              }}
            >
              <Pagination.Prev
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => p - 1)}
                className="border-0 shadow-none"
              >
                <LuChevronLeft />
              </Pagination.Prev>
              {[...Array(totalPages)].map((_, idx) => (
                <Pagination.Item
                  key={idx + 1}
                  active={idx + 1 === currentPage}
                  onClick={() => setCurrentPage(idx + 1)}
                >
                  {idx + 1}
                </Pagination.Item>
              ))}
              <Pagination.Next
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((p) => p + 1)}
                className="border-0 shadow-none"
              >
                <LuChevronRight />
              </Pagination.Next>
            </Pagination>
          </Card.Footer>
        )}
      </Card>

      <ConfirmModal
        show={deletemodalshow}
        onHide={() => !loading && setDeletemodalshow(false)}
        onConfirm={handleDelete}
        title="Delete Customer"
        message="Are you sure you want to delete this customer? This action is permanent and cannot be undone."
        confirmText="Delete Customer"
        variant="danger"
        icon={LuTrash2}
        loading={loading}
      />
    </div>
  );
}